import { createContext, useContext, useMemo, useCallback } from 'react';
import { reportsAPI } from '../services/api';

const ShareContext = createContext(null);

// Share duration options
export const SHARE_DURATION = {
  LATEST: 'latest',
  TWO_WEEKS: '2weeks',
  ONE_MONTH: '1month',
  TWO_MONTHS: '2months',
  THREE_MONTHS: '3months',
  SIX_MONTHS: '6months',
  ALL: 'all',
};

export const SHARE_DURATION_LABELS = {
  [SHARE_DURATION.LATEST]: 'Latest Report Only',
  [SHARE_DURATION.TWO_WEEKS]: 'Last 2 Weeks',
  [SHARE_DURATION.ONE_MONTH]: 'Last 1 Month',
  [SHARE_DURATION.TWO_MONTHS]: 'Last 2 Months',
  [SHARE_DURATION.THREE_MONTHS]: 'Last 3 Months',
  [SHARE_DURATION.SIX_MONTHS]: 'Last 6 Months',
  [SHARE_DURATION.ALL]: 'All Reports',
};

// Link expiration options (in days)
export const LINK_EXPIRATION = {
  ONE_DAY: 1,
  ONE_WEEK: 7,
  TWO_WEEKS: 14,
  ONE_MONTH: 30,
  THREE_MONTHS: 90,
  NEVER: 0,
};

export const LINK_EXPIRATION_LABELS = {
  [LINK_EXPIRATION.ONE_DAY]: '1 Day',
  [LINK_EXPIRATION.ONE_WEEK]: '1 Week',
  [LINK_EXPIRATION.TWO_WEEKS]: '2 Weeks',
  [LINK_EXPIRATION.ONE_MONTH]: '1 Month',
  [LINK_EXPIRATION.THREE_MONTHS]: '3 Months',
  [LINK_EXPIRATION.NEVER]: 'Never Expires',
};

export const ShareProvider = ({ children }) => {
  const generateShareLink = useCallback(async (options) => {
    const {
      reportType = 'project',
      projectId,
      weeklyReportId,
      dueDateFrom,
      dueDateTo,
      excludeNotStarted = true,
      expirationDays = LINK_EXPIRATION.ONE_WEEK,
    } = options;

    const response = await reportsAPI.createShareLink({
      reportType,
      projectId,
      weeklyReportId,
      dueDateFrom,
      dueDateTo,
      excludeNotStarted,
      expirationDays: Number(expirationDays),
    });

    return response.shareUrl;
  }, []);

  const value = useMemo(
    () => ({
      generateShareLink,
      SHARE_DURATION,
      SHARE_DURATION_LABELS,
      LINK_EXPIRATION,
      LINK_EXPIRATION_LABELS,
    }),
    [
      generateShareLink,
    ]
  );

  return (
    <ShareContext.Provider value={value}>
      {children}
    </ShareContext.Provider>
  );
};

export const useShare = () => {
  const context = useContext(ShareContext);
  if (!context) {
    throw new Error('useShare must be used within a ShareProvider');
  }
  return context;
};

export default ShareContext;
